import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';

const statusTextColor = (color) => {
  if (!color) return '#000000';
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#000000' : '#ffffff';
};

// Mix a hex color with white to produce a lightened tint. Used to paint the
// "ESTIMATE STATUS" / "OPS STATUS" / "CLIENT STATUS" label band above the
// substatus bar — the band needs to be a softer, less saturated version of
// the substatus color so the black label is legible and the saturated band
// below pops by contrast. `weight` is how much of the original color to keep
// (0..1); lower = lighter. 0.25 keeps the band recognisably tinted for bright
// colors (purple → lavender, orange → peach) while keeping it light enough
// for dark slate-family colors (UNASSIGNED / COMPLETED) to stay readable.
const lightenColor = (color, weight = 0.25) => {
  if (!color) return '#e2e8f0';
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const mix = (c) => Math.round(c * weight + 255 * (1 - weight));
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

// ─── Open/close transition manager ───────────────────────────────────────
// Two-stage state so the menu can animate out before unmounting:
//   open=true  → mounted=true → next RAF → visible=true   (enter)
//   open=false → visible=false → after exit duration → mounted=false (exit)
// Without `mounted`, React would tear the DOM down before the exit
// transition finishes and you'd see no exit animation.
const ENTER_MS = 180;
const EXIT_MS = 140;

function useTransitionedOpen(open) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two RAFs guarantee the browser paints the closed state before we
      // flip to visible, so the transition actually runs.
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(id2);
      });
      return () => cancelAnimationFrame(id1);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [open]);

  return { mounted, visible };
}

export default function StatusDropdown({ value, onChange, statuses, size = 'sm', onClick, blockRounding = 'rounded-r-lg', blockLabel, chevronBelow = false }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const { mounted, visible } = useTransitionedOpen(open);
  const current = statuses.find(s => s.key === value) || statuses[0] || { key: value, label: value, color: '#94a3b8' };
  const isBlock = size === 'block';

  // Position the (portaled) menu directly under the trigger. Recompute on
  // open so stale rects don't carry over after layout shifts.
  //
  // For BLOCK size — Jaylen's Clients-page mockup — the menu forms a
  // continuous tower with the trigger: same width, same left edge.
  // For pill sizes, keep the older right-edge alignment (menu wider than
  // trigger, right-anchored) which matches places like EstimatesPage.
  //
  // Width must always be EXPLICIT. With `position: fixed` and only one of
  // left/right set, browsers stretch a width:auto box to fill the cross
  // axis, producing huge menus (bug seen 2026-05-14).
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = isBlock ? rect.width : Math.max(180, rect.width);
    const left = isBlock
      ? rect.left              // align menu's left to trigger's left (tower)
      : rect.right - width;    // align menu's right to trigger's right
    setMenuPos({
      top: rect.bottom + 4,
      left,
      width,
    });
  }, [open, isBlock]);

  // Click-outside dismiss. Now has to check BOTH the trigger AND the
  // portaled menu, since the menu is no longer a DOM descendant of the
  // trigger's wrapper. Without this check, clicking inside the menu would
  // immediately dismiss it.
  useEffect(() => {
    if (!mounted) return;
    const handleClick = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mounted]);

  // ESC dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Close on scroll/resize so the portaled menu (which is `position: fixed`)
  // doesn't visually orphan itself from the trigger. Capture-phase scroll
  // listener catches scroll on any ancestor, not just window.
  useEffect(() => {
    if (!open) return;
    const onChange = () => setOpen(false);
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [open]);

  // isBlock is declared earlier (at the top of the function) so the
  // position-calculation effect can use it. Don't redeclare here.
  const sizes = {
    xs: { pill: 'px-2.5 py-0.5 text-[10px]', menu: 'text-xs', width: 'min-w-[140px]' },
    sm: { pill: 'px-3 py-1 text-xs', menu: 'text-sm', width: 'min-w-[180px]' },
    md: { pill: 'px-4 py-1.5 text-sm', menu: 'text-sm', width: 'min-w-[200px]' },
    block: { pill: '', menu: 'text-sm', width: 'min-w-[180px]' },
  };
  const s = sizes[size] || sizes.sm;

  // Menu animates from above-right since the trigger anchors it that way.
  // `position: fixed` because the menu is portaled to <body> and needs
  // viewport-anchored coordinates, not parent-relative ones.
  const menuStyle = {
    position: 'fixed',
    top: menuPos.top,
    left: menuPos.left,
    width: menuPos.width,
    zIndex: 1000, // above modals (100), below toasts which manage own layer
    opacity: visible ? 1 : 0,
    transform: visible
      ? 'translateY(0) scale(1)'
      : 'translateY(-4px) scale(0.96)',
    // Animate origin: block menus stack centered below trigger so center
    // is natural; pill menus open from the trigger's right edge.
    transformOrigin: isBlock ? 'top center' : 'top right',
    transition: visible
      ? `opacity ${ENTER_MS}ms var(--ease-out), transform ${ENTER_MS}ms var(--ease-out)`
      : `opacity ${EXIT_MS}ms var(--ease-out), transform ${EXIT_MS}ms var(--ease-out)`,
    pointerEvents: visible ? 'auto' : 'none',
  };

  // Custom easing on the chevron — beats Tailwind's `transition-transform`
  // default (cubic-bezier(0.4, 0, 0.2, 1) at 150ms) which feels weak.
  const chevronStyle = {
    transition: `transform ${ENTER_MS}ms var(--ease-out)`,
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  };

  // Shared menu node — same JSX for block and pill variants, just with
  // size-appropriate option-row styling. Portaled to <body> so it escapes
  // any ancestor's overflow/stacking-context constraints (fixes the bug
  // where dropdowns inside the Clients-page rows were getting clipped /
  // buried behind sibling rows — 2026-05-14).
  //
  // For the block size, options stack edge-to-edge as a single continuous
  // colored column (no padding gaps, no individual rounding) per Jaylen's
  // mockup. The outer menu container clips with rounded-lg + overflow-hidden
  // so the top and bottom options inherit the rounded corners cleanly.
  // For the pill size, options retain the older inset+rounded look since
  // they're in a smaller dropdown where the colored pill needs whitespace
  // around it.
  const menuNode = mounted && createPortal(
    <div ref={menuRef} role="listbox"
      className={isBlock
        ? 'shadow-xl border border-slate-200 max-h-[380px] overflow-hidden flex flex-col'
        : 'bg-white rounded-xl shadow-xl border border-slate-200 py-1 max-h-[380px] overflow-y-auto'
      }
      style={menuStyle}>
      {statuses.map(st => (
        <button key={st.key} type="button" role="option" aria-selected={st.key === value}
          onClick={(e) => { e.stopPropagation(); onChange(st.key); setOpen(false); }}
          onMouseDown={(e) => e.stopPropagation()}
          className={isBlock
            ? 'w-full px-1.5 py-2.5 font-lemonmilk text-xs text-center uppercase tracking-wide hover:opacity-90 transition-opacity truncate'
            : `w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 ${s.menu}`}
          style={isBlock ? { backgroundColor: st.color, color: statusTextColor(st.color) } : undefined}>
          {isBlock ? (
            st.label
          ) : (
            <>
              <div className="w-full rounded-md px-3 py-1.5 font-semibold text-center"
                style={{ backgroundColor: st.color, color: statusTextColor(st.color) }}>
                {st.label}
              </div>
              {st.key === value && (
                <svg className="w-4 h-4 text-slate-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
              )}
            </>
          )}
        </button>
      ))}
    </div>,
    portalRoot(),
  );

  if (isBlock) {
    // Two visual modes for the block:
    //   • With blockLabel — per Jaylen's 2026-05-14 redesign: the label
    //     sits OUTSIDE the colored block on the row's natural white bg,
    //     reading like a small caption above. The colored block is a
    //     single saturated zone with the substatus + chev. Clean
    //     hierarchy: caption out, headline in.
    //   • Without blockLabel — original full-color single-zone block.
    const subTextColor = statusTextColor(current.color);
    if (blockLabel) {
      return (
        <div className={`relative w-full h-full flex flex-col overflow-hidden ${blockRounding}`}>
          {/* Label sits on white (row bg), no separate fill behind it.
              Tight top padding so it reads as a caption belonging to the
              block below, not floating freely. */}
          <div className="flex-shrink-0 pt-1.5 pb-1 px-3 flex items-center justify-center bg-white">
            <span className="font-lemonmilk text-[10px] uppercase leading-none whitespace-nowrap text-slate-900"
              style={{ letterSpacing: '0.08em' }}>
              {blockLabel}
            </span>
          </div>
          {/* Colored block — the only clickable element. chevronBelow=false
              (default, Clients page style): chevron sits inline-right of
              the label, which reads well on wider columns. chevronBelow=true
              (Estimates page): chevron stacks below, freeing the full
              column width for longer status names. */}
          <button ref={triggerRef} type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(prev => !prev); }}
            onMouseDown={(e) => e.stopPropagation()}
            className={chevronBelow
              ? 'flex-1 flex flex-col items-center justify-center gap-0.5 px-1.5 py-1 cursor-pointer hover:opacity-90 min-w-0 focus:outline-none'
              : 'flex-1 flex items-center justify-center gap-1.5 px-2 cursor-pointer hover:opacity-90 min-w-0 focus:outline-none'}
            style={{ backgroundColor: current.color, color: subTextColor }}
            aria-haspopup="listbox" aria-expanded={open}>
            <span className={`font-lemonmilk uppercase whitespace-nowrap leading-none truncate ${chevronBelow ? 'w-full text-center text-sm' : 'text-base'}`}>
              {current.label}
            </span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="flex-shrink-0" style={chevronStyle}>
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
          {menuNode}
        </div>
      );
    }
    return (
      <div className="relative w-full h-full">
        <button ref={triggerRef} type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(prev => !prev); }}
          onMouseDown={(e) => e.stopPropagation()}
          className={`w-full h-full flex items-center justify-center gap-2 px-4 cursor-pointer hover:opacity-90 ${blockRounding}`}
          style={{ backgroundColor: current.color, color: subTextColor }}
          aria-haspopup="listbox" aria-expanded={open}>
          <span className="font-lemonmilk text-sm uppercase tracking-wide whitespace-nowrap">{current.label}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="flex-shrink-0" style={chevronStyle}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        {menuNode}
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      <button ref={triggerRef} type="button"
        onClick={(e) => { e.stopPropagation(); onClick?.(e); setOpen(!open); }}
        className={`${s.pill} rounded-lg font-semibold cursor-pointer flex items-center gap-1.5 hover:opacity-90 hover:shadow-sm`}
        style={{ backgroundColor: current.color, color: statusTextColor(current.color) }}
        aria-haspopup="listbox" aria-expanded={open}>
        {current.label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={chevronStyle}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {menuNode}
    </div>
  );
}
